import axios from 'axios';

// Determine API URL based on environment
// In production (*.emergent.host or custom domain), use same origin
// In preview (*.preview.emergentagent.com), use the env variable
const getApiUrl = () => {
  // If we're in the browser
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // If on production domain (emergent.host) or custom domain, use same origin
    if (hostname.includes('.emergent.host') || 
        hostname.includes('idmatoi.com') ||
        (!hostname.includes('preview.emergentagent.com') && !hostname.includes('localhost'))) {
      return window.location.origin;
    }
  }
  // Otherwise use the env variable (preview/development)
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Users
export const usersAPI = {
  getAll: () => api.get('/users'),
  getAssignableForClients: () => api.get('/users/assignable-clients'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  archive: (id) => api.patch(`/users/${id}/archive`),
  unarchive: (id) => api.patch(`/users/${id}/unarchive`),
  resetPassword: (id, newPassword) => api.put(`/users/${id}/reset-password`, { new_password: newPassword }),
  updateMyProfile: (data) => api.patch('/users/me/profile', data),
};

// AI Extraction
export const aiAPI = {
  extractLead: (text) => api.post('/ai/extract-lead', { text }),
  createFromLead: (text) => api.post('/ai/create-from-lead', { text }),
  createFromLeadInstant: (text) => api.post('/ai/create-from-lead-instant', { text }),
  // Note Analysis
  analyzeNote: (data) => api.post('/ai/analyze-note', data),
  createTaskFromNote: (suggestion) => api.post('/ai/create-task-from-note', suggestion),
};

// Clients
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  assign: (id, userId) => api.post(`/clients/${id}/assign`, { user_id: userId }),
  delete: (id) => api.delete(`/clients/${id}`),
  addActivity: (id, data) => api.post(`/clients/${id}/activities`, data),
  export: () => api.get('/clients/export', { responseType: 'blob' }),
  getByType: (contactType) => api.get(`/clients/type/${contactType}`),
  getByContactType: (contactType) => api.get(`/clients/type/${contactType}`),
  getClientProfiles: () => api.get('/client-profiles'),
};

// File Upload
export const uploadAPI = {
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadPDF: (formData) => api.post('/upload-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Mini Pelles
export const miniPellesAPI = {
  getAll: () => api.get('/mini-pelles'),
  getOne: (id) => api.get(`/mini-pelles/${id}`),
  create: (data) => api.post('/mini-pelles', data),
  update: (id, data) => api.put(`/mini-pelles/${id}`, data),
  delete: (id) => api.delete(`/mini-pelles/${id}`),
  import: (formData) => api.post('/mini-pelles/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  export: () => api.get('/mini-pelles/export', { responseType: 'blob' }),
};

// Options
export const optionsAPI = {
  getAll: () => api.get('/options'),
  getByCategory: (category) => api.get(`/options/by-category/${category}`),
  create: (data) => api.post('/options', data),
  update: (id, data) => api.put(`/options/${id}`, data),
  delete: (id) => api.delete(`/options/${id}`),
};

export const serviceCatalogAPI = {
  getAll: () => api.get('/service-catalog'),
  create: (data) => api.post('/service-catalog', data),
  update: (id, data) => api.put(`/service-catalog/${id}`, data),
  delete: (id) => api.delete(`/service-catalog/${id}`),
};

// Devis
export const devisAPI = {
  getAll: () => api.get('/devis'),
  getOne: (id) => api.get(`/devis/${id}`),
  create: (data) => api.post('/devis', data),
  update: (id, data) => api.put(`/devis/${id}`, data),
  delete: (id) => api.delete(`/devis/${id}`),
  addActivity: (id, data) => api.post(`/devis/${id}/activities`, data),
  reassign: (id, data) => api.post(`/devis/${id}/reassign`, data),
  sendEmail: (id, data) => api.post(`/devis/${id}/send-email`, data),
  sendRelance: (id, data) => api.post(`/devis/${id}/relance`, data),
  bulkRelance: (devisIds) => api.post('/devis/bulk-relance', { devis_ids: devisIds }),
  bulkCloture: (devisIds) => api.post('/devis/bulk-cloture', { devis_ids: devisIds }),
  updateStatus: (id, status) => api.patch(`/devis/${id}/status`, { status }),
  duplicate: (id) => api.post(`/devis/${id}/duplicate`),
  archive: (id) => api.post(`/devis/${id}/archive`),
  export: () => api.get('/devis/export', { responseType: 'blob' }),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivities: () => api.get('/dashboard/recent-activities'),
};

// Portal Requests
export const portalRequestsAPI = {
  getAll: (params) => api.get('/portal-requests', { params }),
  updateStatus: (id, data) => api.patch(`/portal-requests/${id}/status`, data),
  assign: (id, vendeurId) => api.patch(`/portal-requests/${id}/assign`, { vendeur_id: vendeurId }),
  createDevis: (id) => api.post(`/portal-requests/${id}/create-devis`),
};

// Reports
export const reportsAPI = {
  getActivities: (params) => api.get('/reports/activities', { params }),
  exportActivities: (params) => api.get('/reports/activities/export', { params, responseType: 'blob' }),
};

// Categories
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  initDefaults: () => api.post('/categories/init-defaults'),
};

// Cabinets SNC
export const cabinetsSNCAPI = {
  getAll: () => api.get('/cabinets-snc'),
  getOne: (id) => api.get(`/cabinets-snc/${id}`),
  create: (data) => api.post('/cabinets-snc', data),
  update: (id, data) => api.put(`/cabinets-snc/${id}`, data),
  delete: (id) => api.delete(`/cabinets-snc/${id}`),
};

// Email Templates
export const emailTemplatesAPI = {
  getAll: () => api.get('/email-templates'),
  getOne: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
};

// Pieces Requises (documents required by category)
export const piecesRequisesAPI = {
  getAll: () => api.get('/pieces-requises'),
  getByCategory: (category, params) => api.get(`/pieces-requises/by-category/${category}`, { params }),
  getByClientType: (clientType, params) => api.get(`/pieces-requises/by-client-type/${clientType}`, { params }),
  create: (data) => api.post('/pieces-requises', data),
  update: (id, data) => api.put(`/pieces-requises/${id}`, data),
  delete: (id) => api.delete(`/pieces-requises/${id}`),
};

// Client Profiles (profils clients personnalisés)
export const clientProfilesAPI = {
  getAll: () => api.get('/client-profiles'),
  getOne: (id) => api.get(`/client-profiles/${id}`),
  create: (data) => api.post('/client-profiles', data),
  update: (id, data) => api.put(`/client-profiles/${id}`, data),
  delete: (id) => api.delete(`/client-profiles/${id}`),
};

// Material Types (types de matériel)
export const materialTypesAPI = {
  getAll: () => api.get('/material-types'),
  getOne: (id) => api.get(`/material-types/${id}`),
  create: (data) => api.post('/material-types', data),
  update: (id, data) => api.put(`/material-types/${id}`, data),
  delete: (id) => api.delete(`/material-types/${id}`),
};

// Brands
export const brandsAPI = {
  getAll: () => api.get('/brands'),
};

// Conversations (internal messaging)
export const conversationsAPI = {
  getConversations: () => api.get('/conversations'),
  getConversation: (id) => api.get(`/conversations/${id}`),
  createConversation: (data) => api.post('/conversations', data),
  getMessages: (conversationId, limit = 50) => api.get(`/conversations/${conversationId}/messages?limit=${limit}`),
  sendMessage: (conversationId, data) => api.post(`/conversations/${conversationId}/messages`, data),
  getByEntity: (entityType, entityId) => api.get(`/conversations/by-entity/${entityType}/${entityId}`),
  getUnreadCount: () => api.get('/messages/unread-count'),
};

// Inbound Emails
export const inboundEmailsAPI = {
  getAll: (processed) => api.get(`/inbound-emails${processed !== undefined ? `?processed=${processed}` : ''}`),
  get: (id) => api.get(`/inbound-emails/${id}`),
  reprocess: (id) => api.post(`/inbound-emails/${id}/reprocess`),
};

// Devis Assignment
export const devisAssignmentAPI = {
  assign: (devisId, vendeurId) => api.post(`/devis/${devisId}/assign`, { vendeur_id: vendeurId }),
  getNew: () => api.get('/devis/status/nouveau'),
};

// Appointments/Calendar
export const appointmentsAPI = {
  getAll: (params) => api.get('/appointments', { params }),
  getOne: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
  updateStatus: (id, status) => api.patch(`/appointments/${id}/status`, null, { params: { status } }),
  sendReminder: (id) => api.post(`/appointments/${id}/send-reminder`),
  getUpcomingReminders: (hours = 24) => api.get('/appointments/upcoming-reminders', { params: { hours } }),
};

// Dashboard Stats by Vendeur
export const dashboardStatsAPI = {
  getStatsByVendeur: () => api.get('/dashboard/stats-by-vendeur'),
  getCAMonthly: () => api.get('/dashboard/ca-monthly'),
};

// Tasks/Rappels
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getSummary: () => api.get('/tasks/summary'),
  getOne: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  complete: (id) => api.patch(`/tasks/${id}/complete`),
  // AI suggestions
  getSuggestions: () => api.get('/tasks/ai/suggest-relances'),
  createFromSuggestion: (suggestion) => api.post('/tasks/ai/create-from-suggestion', suggestion),
  // Auto task creation
  autoProcess: () => api.post('/tasks/auto-process'),
  autoProcessSingle: () => api.post('/tasks/auto-process-single'),
  // Categories
  getCategories: () => api.get('/tasks/categories'),
};

// Financing API
export const financingAPI = {
  // Document configs
  getDocConfigs: () => api.get('/financing/doc-configs'),
  getDocConfig: (clientType) => api.get(`/financing/doc-configs/${clientType}`),
  updateDocConfig: (clientType, data) => api.put(`/financing/doc-configs/${clientType}`, data),
  
  // Financing requests
  getRequests: (params) => api.get('/financing/requests', { params }),
  getRequest: (id) => api.get(`/financing/requests/${id}`),
  getRequestByClient: (clientId) => api.get(`/financing/requests/by-client/${clientId}`),
  createRequest: (data) => api.post('/financing/requests', data),
  updateRequest: (id, data) => api.put(`/financing/requests/${id}`, data),
  linkDevis: (requestId, devisId) => api.post(`/financing/requests/${requestId}/link-devis/${devisId}`),
  
  // Documents
  uploadDocument: (requestId, docId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/financing/requests/${requestId}/upload/${docId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  validateDocument: (requestId, docId) => api.post(`/financing/requests/${requestId}/validate/${docId}`),
  rejectDocument: (requestId, docId, reason) => api.post(`/financing/requests/${requestId}/reject/${docId}?reason=${encodeURIComponent(reason)}`),
  toggleDocumentChecked: (requestId, docId) => api.post(`/financing/requests/${requestId}/toggle-checked/${docId}`),
  downloadDocument: (requestId, docId) => api.get(`/financing/requests/${requestId}/download/${docId}`, { responseType: 'blob' }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params = {}) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),
};

// Stats API
export const statsAPI = {
  getDevisToAssignCount: () => api.get('/stats/devis-to-assign'),
};

// CMS Settings API
export const cmsSettingsAPI = {
  get: () => api.get('/cms/settings'),
  update: (settings) => api.put('/cms/settings', settings),
};

// Messaging API
export const messagingAPI = {
  getTemplates: (params = {}) => api.get('/message-templates', { params }),
  createTemplate: (data) => api.post('/message-templates', data),
  updateTemplate: (id, data) => api.put(`/message-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/message-templates/${id}`),
  sendEmail: (data) => api.post('/messaging/send-email', data),
  sendSMS: (data) => api.post('/messaging/send-sms', data),
  getHistory: (entityType, entityId) => api.get(`/messaging/history/${entityType}/${entityId}`),
};

// AI Sales Agent API
export const aiAgentAPI = {
  chat: (message, conversationId = null) => api.post('/ai-agent/chat', { message, conversation_id: conversationId }),
  getSummary: () => api.get('/ai-agent/summary'),
  getActivitiesToday: () => api.get('/ai-agent/activities-today'),
  getConversations: () => api.get('/ai-agent/conversations'),
  triggerNotifications: () => api.post('/ai-agent/trigger-notifications'),
  triggerReport: () => api.post('/ai-agent/trigger-report'),
};

// Blog AI API
export const blogAPI = {
  getArticles: (status = null, limit = 20, skip = 0) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('limit', limit);
    params.append('skip', skip);
    return api.get(`/blog/articles?${params.toString()}`);
  },
  getArticle: (id) => api.get(`/blog/articles/${id}`),
  generateArticle: (topic, keywords = []) => api.post('/blog/articles/generate', { topic, keywords }),
  updateArticle: (id, data) => api.put(`/blog/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/blog/articles/${id}`),
  publishArticle: (id) => api.post(`/blog/articles/${id}/publish`),
  unpublishArticle: (id) => api.post(`/blog/articles/${id}/unpublish`),
  improveArticle: (id, instructions) => api.post(`/blog/articles/${id}/improve`, { instructions }),
  suggestTopics: (count = 5) => api.get(`/blog/topics/suggest?count=${count}`),
  triggerAutoGenerate: () => api.post('/blog/generate-scheduled'),
};

// Public Blog API (no auth)
export const publicBlogAPI = {
  getArticles: (limit = 10, skip = 0) => api.get(`/public/blog/articles?limit=${limit}&skip=${skip}`),
  getArticleBySlug: (slug) => api.get(`/public/blog/articles/${slug}`),
  getRecentArticles: (limit = 3) => api.get(`/public/blog/recent?limit=${limit}`),
};

// Emailing API
export const emailingAPI = {
  // Templates prédéfinis
  getTemplates: (campaignType = null) => {
    const params = campaignType ? `?campaign_type=${campaignType}` : '';
    return api.get(`/emailing/templates${params}`);
  },
  getTemplate: (id) => api.get(`/emailing/templates/${id}`),
  
  // Campaign types
  getCampaignTypes: () => api.get('/emailing/campaign-types'),
  
  // Campaigns CRUD
  getCampaigns: (status = null, limit = 20, skip = 0) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('limit', limit);
    params.append('skip', skip);
    return api.get(`/emailing/campaigns?${params.toString()}`);
  },
  getCampaign: (id) => api.get(`/emailing/campaigns/${id}`),
  createCampaign: (data) => api.post('/emailing/campaigns', data),
  updateCampaign: (id, data) => api.put(`/emailing/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/emailing/campaigns/${id}`),
  duplicateCampaign: (id) => api.post(`/emailing/campaigns/${id}/duplicate`),
  
  // AI Generation
  generateContent: (campaignType, context = null, tone = 'professionnel', customInstructions = null) => 
    api.post('/emailing/generate', { campaign_type: campaignType, context, tone, custom_instructions: customInstructions }),
  improveContent: (currentContent, instructions) => 
    api.post('/emailing/improve', { current_content: currentContent, instructions }),
  suggestSubjects: (campaignType, context = null, count = 5) => 
    api.post(`/emailing/suggest-subjects?campaign_type=${campaignType}&count=${count}${context ? `&context=${encodeURIComponent(context)}` : ''}`),
  
  // Recipients
  previewRecipients: (filterType, filterParams = {}) => 
    api.get(`/emailing/recipients/preview?filter_type=${filterType}&filter_params=${encodeURIComponent(JSON.stringify(filterParams))}`),
  searchContacts: (query = '', limit = 20) => 
    api.get(`/emailing/contacts/search?query=${encodeURIComponent(query)}&limit=${limit}`),
  
  // Send
  sendCampaign: (id, testMode = false, testEmail = null) => 
    api.post(`/emailing/campaigns/${id}/send`, { test_mode: testMode, test_email: testEmail }),
  getCampaignStats: (id) => api.get(`/emailing/campaigns/${id}/stats`),
  
  // Automation Rules
  getAutomationConfig: () => api.get('/emailing/automation/config'),
  getAutomationRules: (campaignId = null) => {
    const params = campaignId ? `?campaign_id=${campaignId}` : '';
    return api.get(`/emailing/automation/rules${params}`);
  },
  getAutomationRule: (id) => api.get(`/emailing/automation/rules/${id}`),
  createAutomationRule: (data) => api.post('/emailing/automation/rules', data),
  updateAutomationRule: (id, data) => api.put(`/emailing/automation/rules/${id}`, data),
  deleteAutomationRule: (id) => api.delete(`/emailing/automation/rules/${id}`),
  toggleAutomationRule: (id, enabled) => api.post(`/emailing/automation/rules/${id}/toggle?enabled=${enabled}`),
  executeAutomationRule: (id) => api.post(`/emailing/automation/rules/${id}/execute`),
};

export default api;
